import React, { useState, useEffect } from 'react';
import { StickyNote } from 'lucide-react';
import { useEntityNotes } from '../../hooks/useNotesV2';
import ViewNotesModal from './ViewNotesModal';
import { getEntityDisplayName } from '../../utils/entityDisplayUtils';

interface NoteCountBadgeProps {
  entityType: string;
  entityId: string;
  entityDisplayName?: string;
  workspaceId?: string;
  className?: string;
  showNotesOnClick?: boolean;
  size?: 'sm' | 'md';
  variant?: 'badge' | 'button';
}

const NoteCountBadge: React.FC<NoteCountBadgeProps> = ({
  entityType,
  entityId,
  entityDisplayName: providedDisplayName,
  workspaceId,
  className = '',
  showNotesOnClick = true,
  size = 'sm',
  variant = 'badge'
}) => {
  const [showModal, setShowModal] = useState(false);
  const [entityDisplayName, setEntityDisplayName] = useState<string>(providedDisplayName || '');
  const { notes, isLoading } = useEntityNotes(entityType, entityId, workspaceId, 100);

  // Fetch entity display name (only if not provided via prop)
  useEffect(() => {
    if (providedDisplayName) {
      setEntityDisplayName(providedDisplayName);
      return;
    }
    
    const fetchDisplayName = async () => {
      const displayName = await getEntityDisplayName(entityType, entityId, workspaceId);
      setEntityDisplayName(displayName);
    };
    
    if (entityType && entityId) {
      fetchDisplayName();
    }
  }, [entityType, entityId, workspaceId, providedDisplayName]);

  const noteCount = notes.length;

  if (isLoading) {
    return (
      <div className={`inline-flex items-center px-2 py-1 text-xs bg-white dark:bg-background-dark-secondary text-text-tertiary dark:text-text-dark-tertiary rounded-full animate-pulse ${className}`}>
        <StickyNote className={size === 'sm' ? 'w-3 h-3 mr-1' : 'w-4 h-4 mr-1'} />
        <span>...</span>
      </div>
    );
  }

  const handleClick = () => {
    if (showNotesOnClick) {
      setShowModal(true);
    }
  };

  // Button variant (used in Calendar and Bill Detail)
  if (variant === 'button') {
    return (
      <>
        <button
          onClick={handleClick}
          className={`btn btn-primary inline-flex items-center gap-2 ${showNotesOnClick ? 'cursor-pointer' : 'cursor-default'} ${className}`}
          disabled={!showNotesOnClick}
          title={noteCount > 0 ? `View ${noteCount} note${noteCount === 1 ? '' : 's'}` : 'Add note'}
        >
          <StickyNote className="w-4 h-4" />
          <span>Notes</span>
          {noteCount > 0 && (
            <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold bg-white bg-opacity-20 rounded-full">
              {noteCount}
            </span>
          )}
        </button>

        {showModal && (
          <ViewNotesModal
            isOpen={showModal}
            onClose={() => setShowModal(false)}
            notes={notes.map(note => ({
              id: note.id,
              title: note.title,
              content: note.content,
              created_at: note.created_at,
              updated_at: note.updated_at,
              include_in_reports: note.include_in_reports,
              author_name: note.author_name || 'Unknown',
              tags: note.tags || []
            }))}
            entityType={entityType}
            entityId={entityId}
            entityDisplayName={entityDisplayName || `${entityType}: ${entityId}`}
            workspaceId={workspaceId}
          />
        )}
      </>
    );
  }

  // Badge variant (default)
  return (
    <>
      <button
        onClick={handleClick}
        className={`
          inline-flex items-center rounded-full transition-colors
          ${size === 'sm' ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm'}
          ${noteCount > 0 
            ? 'bg-primary-100 dark:bg-primary-900/20 text-primary-800 dark:text-primary-200 hover:bg-primary-200 dark:hover:bg-primary-900/40' 
            : 'bg-white dark:bg-background-dark-secondary text-text-tertiary dark:text-text-dark-tertiary hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary'
          }
          ${showNotesOnClick ? 'cursor-pointer' : 'cursor-default'}
          ${className}
        `}
        disabled={!showNotesOnClick}
        title={noteCount > 0 ? `View ${noteCount} note${noteCount === 1 ? '' : 's'}` : 'Add note'}
      >
        <StickyNote className={size === 'sm' ? 'w-3 h-3 mr-1' : 'w-4 h-4 mr-1'} />
        <span className="font-medium">{noteCount}</span>
      </button>

      {showModal && (
        <ViewNotesModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          notes={notes.map(note => ({
            id: note.id,
            title: note.title,
            content: note.content,
            created_at: note.created_at,
            updated_at: note.updated_at,
            include_in_reports: note.include_in_reports,
            author_name: note.author_name || 'Unknown',
            tags: note.tags || []
          }))}
          entityType={entityType}
          entityId={entityId}
          entityDisplayName={entityDisplayName || `${entityType}: ${entityId}`}
          workspaceId={workspaceId}
        />
      )}
    </>
  );
};

export default NoteCountBadge;
